import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { ONBOARDING_WIZARD_STEPS } from "./constants";
import { useIsMobile } from "./useIsMobile";
import { useKeyboardOpen } from "./useKeyboardOpen";

export function OnboardingFormLayout({
  step,
  backTo,
  children,
}: {
  step: "profile" | "preferences" | "match";
  backTo?: string;
  children: ReactNode;
}) {
  return (
    <div className="onboarding-form-layout">
      <OnboardingProgress current={step} embedded pageChrome backTo={backTo} />
      <div className="onboarding-form-main">{children}</div>
    </div>
  );
}

export function OnboardingProgress({
  current,
  className = "",
  embedded = false,
  pageChrome = false,
  backTo,
}: {
  current: "profile" | "preferences" | "match";
  className?: string;
  embedded?: boolean;
  pageChrome?: boolean;
  backTo?: string;
}) {
  const mobile = useIsMobile();
  const keyboardOpen = useKeyboardOpen();
  const currentStep = ONBOARDING_WIZARD_STEPS.find((step) => step.id === current) ?? ONBOARDING_WIZARD_STEPS[0];
  const currentIndex = ONBOARDING_WIZARD_STEPS.findIndex((step) => step.id === current);
  const progressRatio = currentIndex / Math.max(ONBOARDING_WIZARD_STEPS.length - 1, 1);

  if (mobile) {
    const progressNav = (
      <nav
        className={[
          "onboarding-progress",
          "onboarding-progress--compact",
          embedded ? "onboarding-progress--embedded" : "",
          pageChrome ? "onboarding-progress--page-chrome" : "",
          !pageChrome && keyboardOpen ? "is-keyboard-open" : "",
          className,
        ].filter(Boolean).join(" ")}
        aria-label="Onboarding progress"
        style={{ "--onboarding-progress-ratio": String(progressRatio) } as CSSProperties}
      >
        <div className="onboarding-progress-compact-toolbar">
          {backTo ? (
            <Link className="icon-button onboarding-progress-back" to={backTo} aria-label="Back">
              <ArrowLeft size={20} />
            </Link>
          ) : null}
          <div className="onboarding-progress-compact-main">
            <p className="onboarding-progress-compact-title">
              <span className="onboarding-progress-compact-count">
                Step {currentIndex + 1} of {ONBOARDING_WIZARD_STEPS.length}
              </span>
              <span className="onboarding-progress-compact-current">{currentStep.label}</span>
            </p>
            <div className="onboarding-progress-track" aria-hidden="true">
              {ONBOARDING_WIZARD_STEPS.map((step, index) => {
                const done = index < currentIndex;
                const active = index === currentIndex;
                return (
                  <span
                    className={[
                      "onboarding-progress-track-segment",
                      done ? "is-done" : "",
                      active ? "is-active" : "",
                    ].filter(Boolean).join(" ")}
                    key={step.id}
                  />
                );
              })}
            </div>
          </div>
        </div>
        <p className="sr-only">{`${currentStep.label}. ${currentStep.hint}`}</p>
      </nav>
    );

    if (pageChrome) {
      return (
        <div
          className={[
            "onboarding-progress-sticky-shell",
            keyboardOpen ? "is-keyboard-open" : "",
          ].filter(Boolean).join(" ")}
        >
          {progressNav}
        </div>
      );
    }

    return progressNav;
  }

  return (
    <nav
      className={`onboarding-progress ${className}`.trim()}
      aria-label="Onboarding progress"
      style={{ "--onboarding-progress-ratio": String(progressRatio) } as CSSProperties}
    >
      <div className="onboarding-progress-summary">
        <p className="onboarding-progress-kicker">Getting started</p>
        <p className="onboarding-progress-title">
          <span className="onboarding-progress-count">
            Step {currentIndex + 1} of {ONBOARDING_WIZARD_STEPS.length}
          </span>
          <span className="onboarding-progress-current">{currentStep.label}</span>
        </p>
        {currentStep.hint && (
          <p className="onboarding-progress-subtitle">{currentStep.hint}</p>
        )}
      </div>

      <ol className="onboarding-progress-steps">
        {ONBOARDING_WIZARD_STEPS.map((step, index) => {
          const done = index < currentIndex;
          const active = index === currentIndex;
          return (
            <li
              className={`onboarding-progress-step ${done ? "is-done" : ""} ${active ? "is-active" : ""} ${!done && !active ? "is-upcoming" : ""}`.trim()}
              key={step.id}
              aria-current={active ? "step" : undefined}
            >
              <span className="onboarding-progress-marker" aria-hidden="true">
                {done ? <Check size={14} strokeWidth={2.75} /> : index + 1}
              </span>
              <span className="onboarding-progress-step-label">{step.label}</span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function OnboardingReadyMoment({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="onboarding-ready app-state-screen app-state-screen--success">
      <div className="app-state-screen-icon" aria-hidden="true">
        <Check size={34} />
      </div>
      <div className="app-state-screen-copy">
        <h2>You are ready</h2>
        <p>Your profile and preferences are set. Yara will introduce one person at a time.</p>
      </div>
      <div className="app-state-screen-actions">
        <button className="button primary" type="button" onClick={onContinue}>Go to Match</button>
      </div>
    </div>
  );
}

export function FormSectionHead({
  icon: Icon,
  title,
  description,
  tone,
}: {
  icon?: React.ComponentType<{ size?: number }>;
  title: string;
  description?: string;
  tone?: string;
}) {
  return (
    <div className={`profile-form-section-head profile-form-section-head--elevated ${tone ? `profile-form-section-head--${tone}` : ""}`.trim()}>
      {Icon && (
        <span className="profile-form-section-icon" aria-hidden="true">
          <Icon size={18} />
        </span>
      )}
      <div>
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </div>
    </div>
  );
}
